import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useGeolocation } from '../hooks/useGeolocation';
import { submitLead } from '../services/api';
import { validateEmail, validatePhone, sanitizePhoneInput } from '../utils/validation';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';

const ContactUs = () => {
  const { toast } = useToast();
  const { geolocationData, countryCode } = useGeolocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryDialCode: '1',
    country: 'US',
    service: 'trademark',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(formData.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone
    if (!validatePhone(formData.phone)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number (7-15 digits).',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phone,
        countryDialCode: formData.countryDialCode,
        country: formData.country || countryCode,
        message: formData.message,
        description: `Subject: ${formData.subject}. Service: ${formData.service}. ${formData.message}`,
      };

      const result = await submitLead(leadData, geolocationData);

      if (result.success && result.shouldRedirect && result.customerId) {
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else if (result.success) {
        toast({
          title: 'Success!',
          description: result.message || 'Message sent successfully!',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryDialCode: '1',
          country: 'US',
          service: 'trademark',
          subject: '',
          message: '',
        });
      } else {
        // Show error
        toast({
          title: 'Error',
          description: result.message || 'Failed to send message. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['611 Wilshire Blvd #900', 'Los Angeles, CA 90017'],
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+1 (415) 915-6981', 'Mon-Fri: 9:00 AM - 6:00 PM EST'],
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['info@finetrademark.com', 'support@finetrademark.com'],
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 10:00 AM - 2:00 PM'],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
            Get In Touch
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom duration-700">
            Have questions about our services? We're here to help protect your brand.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="section-heading mb-6">Send Us a Message</h2>
              <p className="body-regular mb-8">
                Fill out the form below and our team will get back to you within 24 hours. Take advantage of our special $45 trademark registration offer!
              </p>
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Field */}
                    <div>
                      <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                        Full Name *
                      </label>
                      <Input
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-12 border-2 focus:border-[#E5B80B]"
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12 border-2 focus:border-[#E5B80B]"
                      />
                    </div>

                    {/* Contact Number with Country Code */}
                    <div>
                      <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                        Contact Number *
                      </label>
                      <div className="flex gap-3">
                        <select
                          value={formData.countryDialCode}
                          onChange={(e) => {
                            const selectedOption = e.target.options[e.target.selectedIndex];
                            setFormData({ 
                              ...formData, 
                              countryDialCode: e.target.value,
                              country: selectedOption.getAttribute('data-country')
                            });
                          }}
                          className="h-12 px-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E5B80B] focus:border-[#E5B80B] font-semibold text-[#1E1E1E] w-28 bg-white"
                        >
                          <option value="1" data-country="US">🇺🇸 +1</option>
                          <option value="44" data-country="GB">🇬🇧 +44</option>
                          <option value="91" data-country="IN">🇮🇳 +91</option>
                          <option value="61" data-country="AU">🇦🇺 +61</option>
                          <option value="86" data-country="CN">🇨🇳 +86</option>
                          <option value="81" data-country="JP">🇯🇵 +81</option>
                          <option value="49" data-country="DE">🇩🇪 +49</option>
                          <option value="33" data-country="FR">🇫🇷 +33</option>
                          <option value="971" data-country="AE">🇦🇪 +971</option>
                          <option value="65" data-country="SG">🇸🇬 +65</option>
                        </select>
                        <Input
                          type="tel"
                          placeholder="Enter phone number"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: sanitizePhoneInput(e.target.value) })}
                          required
                          className="h-12 flex-1 border-2 focus:border-[#E5B80B]"
                        />
                      </div>
                    </div>

                    {/* Description Field */}
                    <div>
                      <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                        Description
                      </label>
                      <Input
                        placeholder="How can we help you?"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        className="h-12"
                      />
                    </div>

                    <div>
                      <Textarea
                        placeholder="Tell us about your requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        className="min-h-32 border-2 focus:border-[#E5B80B] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold h-12 text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="mr-2 h-5 w-5" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E1E1E] mb-4 sm:mb-6">Contact Information</h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Reach out to us through any of the following channels. We're committed to providing prompt and helpful responses to all inquiries.
              </p>
              <div className="space-y-4 sm:space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-start space-x-3 sm:space-x-4">
                          <div className="p-2 sm:p-3 bg-[#E5B80B]/10 rounded-lg flex-shrink-0">
                            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#E5B80B]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-semibold text-[#1E1E1E] mb-1 sm:mb-2">{info.title}</h3>
                            {info.details.map((detail, idx) => (
                              <p key={idx} className="text-sm sm:text-base text-gray-600 break-words">
                                {detail}
                              </p>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Special Offer Card */}
              <Card className="mt-6 sm:mt-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09] border-0">
                <CardContent className="p-6 sm:p-8 text-center">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1E1E1E] mb-3 sm:mb-4">Special Offer</h3>
                  <p className="text-base sm:text-lg text-[#1E1E1E]/90 mb-4 sm:mb-6 px-2">
                    Get your trademark registered for just $45 - Limited time offer!
                  </p>
                  <Button className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 text-base sm:text-lg">
                    Register Your Trademark
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1E1E1E] mb-4">Visit Our Office</h2>
            <p className="text-lg text-gray-600">Located in the heart of New York City</p>
          </div>
          <div className="h-96 bg-gray-300 rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976383964465!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
